import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Table, Card, Pagination, Modal, message, Dropdown, Tooltip, Space } from "antd";
import { CheckCircleOutlined, CheckOutlined, CloseCircleOutlined, CloseOutlined, EditOutlined, ExclamationCircleOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { ItemDetailMobile } from "../../components/ItemDetailMobile";
import { isArrayNonEmpty } from "../../util/ApiHelper";
import ListEmptyComponent from "../../components/ListEmptyComponent/ListEmptyComponent";
import { useHistory } from "react-router-dom";
import { formatDate } from "../../util/helper";
import { useDispatch, useSelector } from "react-redux";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { BUSINESS_REQUEST_STATUS } from "../../util/Constant";

const BusinessCardRequestsExporter = () => {
  const { isMobile } = useGetBreakpoints();
  const [requestsData, setRequestsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const history = useHistory();
  const loading = useSelector(({ common }) => common.loading);
  const dispatch = useDispatch();
  
  useEffect(() => {
    fetchReceivedRequests();
  }, [dispatch]);

  const fetchReceivedRequests = async () => {
    dispatch(fetchStart());
    try {
      const response = await axiosInstance.get(`${BASE_API_CONFIG.baseURL}/business-card-request/user/forwarders`);
      const formattedForwarders = response.data.map((forwarder) => ({
        id: forwarder.id,
        forwarderName: forwarder.forwarder.companyName,
        createdDate: forwarder.createdAt,
        status: forwarder.status,
        message: forwarder.comments,
      }));
      setRequestsData(formattedForwarders);
      setTotalCount(response.data.length);
    } catch (error) {
      console.error("Error fetching requests:", error);
      message.error("Failed to load requests");
    } finally {
      dispatch(fetchSuccess());
    }
  };

  const onShowSizeChange = useCallback(
    (current, pageSize) => {
      setCurrentPage(current);
      setPageSize(pageSize);
    },
    []
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "#faad14";
      case "Approved":
        return "#52c41a";
      case "Rejected":
        return "#f5222d";
      default:
        return "#1890ff";
    }
  };

  const updateStatus = async (id, status, forwarderName) => {
  try {
    await axiosInstance.patch(
      `${BASE_API_CONFIG.baseURL}/business-card-request/${id}/status`,
      { status }
    );

    message.success(
      `Request from ${forwarderName} ${status.toLowerCase()} successfully!`
    );
    fetchReceivedRequests();
  } catch (error) {
    message.error(
      error?.response?.data?.message || `Failed to ${status.toLowerCase()} request`
    );
  }
};


  const handleApprove = useCallback((record) => {
    Modal.confirm({
      title: "Approve Business Card Request",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to approve the request from ${record.forwarderName}?`,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        await updateStatus(record.id, "Approved", record.forwarderName);
      },
    });
  }, []);

  const handleReject = useCallback((record) => {
    Modal.confirm({
      title: "Reject Business Card Request",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to reject the request from ${record.forwarderName}?`,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { danger: true },
      onOk: async () => {
        await updateStatus(record.id, "Rejected", record.forwarderName);
      },
    });
  }, []);

  const actionRenderer = useCallback(
    (record) => (
      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
        {record.status === "Pending" ? (
          <>
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleApprove(record)}
              style={{ marginRight: "8px" }}
            >
              Approve
            </Button>
            <Button
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => handleReject(record)}
            >
              Reject
            </Button>
          </>
        ) : (
          <span style={{ color: getStatusColor(record.status), fontWeight: "bold" }}>
            {record.status}
          </span>
        )}
      </div>
    ),
    [handleApprove, handleReject]
  );

  const viewDetails = useCallback(
    (record) => (
      <EyeOutlined
        style={{ fontSize: "18px", color: "#1890ff", display: "flex", justifyContent: "center" }}
        onClick={(e) => {
          e.stopPropagation();
          history.push(`/business-card-requests-exporter/view-details/${record.id}`);
        }}
      />
    ), [history]
  );

  const navigateToDetails = useCallback(
    (id) => {
      history.push(`/business-card-requests-exporter/view-details/${id}`)
    }
  ,[history])

    const handleRowClick = useCallback((id) => {
       history.push(`/business-card-requests-exporter/view-details/${id}`)
    }, []);

  const moreActionRenderer = useCallback(
    (record) => {
      const isPending = record.status === BUSINESS_REQUEST_STATUS.PENDING;

      return (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Space size="middle">
            <Tooltip title="Forwarder Details">
              <Button
                size="small"
                shape="circle"
                icon={<EyeOutlined />}
                onClick={() => navigateToDetails(record?.id)}
              />
            </Tooltip>

            <Tooltip title="Approve">
              <Button
                size="small"
                shape="circle"
                icon={<CheckOutlined />}
                disabled={!isPending}
                style={
                  isPending
                    ? { background: "#52c41a", color: "#fff", border: "#102306" }
                    : {}
                }
                onClick={() => handleApprove(record)}
              />
            </Tooltip>

            <Tooltip title="Reject">
              <Button
                size="small"
                shape="circle"
                icon={<CloseOutlined />}
                disabled={!isPending}
                danger={isPending}
                onClick={() => handleReject(record)}
              />
            </Tooltip>
          </Space>
        </div>
      );
    },
    [navigateToDetails, handleApprove, handleReject]
  );

  const columns = useMemo(
    () => [
      {
        title: "Date",
        key: "createdDate",
        width: "120px",
        render: (record) => <div>{formatDate(record?.createdDate)}</div>,
      },
      {
        title: "Forwarder Company Name",
        key: "forwarderName",
        width: "240px",
        render: (record) => (
          <div>{record?.forwarderName}</div>
        ),
      },
      {
        title: "Status",
        key: "status",
        width: "120px",
        render: (record) => (
          <div>
            <span
              style={{
                color: getStatusColor(record?.status),
                fontWeight: "bold",
                padding: "4px 8px",
                borderRadius: "4px",
                background: getStatusColor(record?.status) + "20"
              }}>
              {record?.status}
            </span>
          </div>
        ),
      },
      {
        title: "Action",
        key: "action",
        width: "150px",
        align: "center",
        render: moreActionRenderer,
      }
    ],
    [actionRenderer, viewDetails,moreActionRenderer]
  );

  const renderContent = useCallback(
    (data, count) => {
      return isMobile ? (
        <>
          <Card bodyStyle={{ padding: 0 }}>
            {data.map((record) => (
              <div
                key={record.id}
                className="item-detail-mobile gx-flex-row gx-pointer gx-border-bottom gx-align-items-center"
                onClick={() => {
                  handleRowClick(record.id);
                }}
              >
                <div className="item-details gx-flex-1 gx-flex-column">
                  <div className="item-posted-date">{record.createdDate}</div>
                  <div className="item-primary-row highlighted-column">
                    <div className="item-primary-text">{record.forwarderName}</div>
                  </div>
                  <div
                    className="item-secondary-row"
                    style={{ fontSize: "12px", color: "#999", marginBottom: "8px" }}
                  >
                    {record.companyEmail}
                  </div>
                  {/* {record.message && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginBottom: "8px",
                        padding: "8px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                      }}
                    >
                      {record.message}
                    </div>
                  )} */}
                  <div
                    className="item-additional-content gx-flex-row gx-justify-content-between gx-align-items-center"
                    style={{ 
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: "8px" }}
                  >
                    <span
                      style={{
                        color: getStatusColor(record?.status),
                        fontWeight: "bold",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: getStatusColor(record?.status) + "20",
                      }}
                    >
                      {record?.status}
                    </span>
                    {moreActionRenderer(record)}
                  </div>
                </div>
              </div>
            ))}
          </Card>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={count}
            onChange={onShowSizeChange}
            showSizeChanger
            style={{ marginTop: 16, textAlign: "right" }}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
          />
        </>
      ) : (
        <Table
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          className="gx-table-responsive business-card-requests-table myexim-table"
          columns={columns}
          dataSource={data}
          pagination={{
            position: ["bottomRight"],
            showSizeChanger: true,
            pageSize: pageSize,
            total: count,
            onChange: onShowSizeChange,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                handleRowClick(record?.id);
              },
            };
          }}
          rowKey="id"
        />
      );
    },
    [
      isMobile,
      columns,
      currentPage,
      pageSize,
      onShowSizeChange,
      handleApprove,
      handleReject,
    ]
  );

  return (
    <>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className="myexim-table-title">
              Business Card Requests
            </div>
          </div>
        }
        style={{ width: "100%" }}
      >
        {isArrayNonEmpty(requestsData) ? (
          renderContent(requestsData, totalCount)
        ) : (
          !loading && (
            <ListEmptyComponent
              message="No Business Card Requests Received"
            />
          )
        )}
      </Card>
    </>
  );
};

export default BusinessCardRequestsExporter;
